function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

import Backbone from '@polleverywhere/backbone';
import _ from 'underscore';
var FTP_POLLING_INTERVAL = 5000;
var DEFAULT_CHART_POLLING_INTERVAL = 3000;
/**
 * Dictionary of update modes. When updater is not running, it holds a '' value.
 * @type {Object}
 */

var UPDATE_MODE = {
  firehose: 'firehose',
  polling: 'polling'
};
/**
 * Short polling re-hydrates the activity model based on time interval. This
 * function returns query param attributes to be appended to the request URL.
 * @return {Object} Key/value query params as an object.
 */

function shortPollParams(self) {
  var obj = {};

  if (self.serviceAreaId > 0 && !self.flashOptions.isSegmented()) {
    if (self.activity.type() !== 'discourse') {
      obj.service_area_id = self.serviceAreaId;
    }
  }

  if (self.activity.type() === 'free_text_poll' || self.activity.type() === 'discourse') {
    obj.limit = self.flashOptions.get('responseLimit') || 25;
  }

  return obj;
}
/**
 * Determines if the provided model must use short polling instead of Firehose.
 * @return {boolean} True indicates model needs short polling for live updates.
 */


function forceShortPolling(model, serviceAreaId) {
  var isDiscourse = model.type() === 'discourse';
  var isSegmented = model.flashOptions && model.flashOptions().isSegmented(); // Discourse and segmented polls always use short-polling

  return isDiscourse || isSegmented || serviceAreaId > 0;
}

function shortPollModel(self, config, interval) {
  clearTimeout(self.intervalTimeout); // Backbone model fetch data

  var data = shortPollParams(self);
  var comparisonPollKey = self.flashOptions && self.flashOptions.isSegmented() && self.flashOptions.get('teamPollPermalink');
  var fetchPromise;

  if (comparisonPollKey) {
    fetchPromise = self.activity.comparisons.add({
      presenter_key: comparisonPollKey
    }).fetch({
      data: data
    });
  } else {
    fetchPromise = self.activity.fetch({
      data: data
    });
  }

  return fetchPromise.fail(config.error).always(function () {
    self.intervalTimeout = _.delay(shortPollModel, interval, self, config, interval);
  });
}

function handleFirehoseError() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  args.splice(0, 0, 'error');
  this.trigger.apply(this, args);
}
/*
 * Activity Updater enables/disables live updates for an activity either through
 * short polling or Firehose based on varying circumstances. This allows
 * consumers of activities to enable live updates with this common interface.
 * @param {Object} options
 * @param {Backbone.Model} options.activity Activity model
 * @param {string} [options.mode='firehose'] Updater attempts to use
 *    this mode whenever starting to get live updates.
 * @param {number} [options.serviceAreaId=null] Service area to use when polling
 *    json data.
 * @param {boolean} [options.multiplex=true] Use multiplex firehose connections.
 * @event connect Connected
 * @event disconnect Disconnected
 * @event error Error occurred
 */


var ActivityUpdater = /*#__PURE__*/function () {
  function ActivityUpdater(options) {
    if (options === void 0) {
      options = {};
    }

    _.extend(this, Backbone.Events);

    this.options = _.defaults(options, {
      mode: UPDATE_MODE.firehose,
      serviceAreaId: null,
      multiplex: true
    });
    /**
     * Holds the current mode of the updater, one of three values:
     * 'firehose', 'polling' or '' for when updater is stopped.
     * @type {string}
     */

    this.currentMode = '';
    this.flashOptions = typeof this.activity.flashOptions === 'function' ? this.activity.flashOptions() : undefined;
  }

  var _proto = ActivityUpdater.prototype;
  /**
   * Unsubscribe from Firehose live updates.
   * @return {Promise}
   */

  _proto.stop = function stop() {
    if (this.activity.type() === 'survey') {
      return;
    }

    this.currentMode = '';
    clearTimeout(this.intervalTimeout);
    return this.activity.instance.stop();
  }
  /**
   * Subscribe to Firehose live updates or short polling based on conditions.
   * @return {Promise}
   */
  ;

  _proto.start = function start() {
    var _this = this;

    if (this.activity.type() === 'survey') {
      return;
    }

    var config = {
      multiplex: this.multiplex,
      connected: function connected() {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }

        args.splice(0, 0, 'connect');
        return _this.trigger.apply(_this, args);
      },
      disconnected: function disconnected() {
        for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
          args[_key3] = arguments[_key3];
        }

        args.splice(0, 0, 'disconnect');

        _this.trigger.apply(_this, args);

        handleFirehoseError.call.apply(handleFirehoseError, [_this].concat(args));
      },
      error: handleFirehoseError.bind(this),
      failed: function failed() {
        /*
         * With the default failed handler from the Firehose client
         * we just throw a string saying that we couldn't connect
         * Let's downgrade from `error` to `info` and try to provide a bit
         * more context. If we find that they're important we can upgrade
         * back to `error`
         */
        return _this.trigger('error', 'Connection failed', {
          activity: _this.activity.attributes
        });
      }
    };
    var useShortPolling = this.options.mode === UPDATE_MODE.polling || forceShortPolling(this.activity, this.serviceAreaId);

    if (useShortPolling) {
      var interval = this.activity.type() === 'free_text_poll' ? FTP_POLLING_INTERVAL : DEFAULT_CHART_POLLING_INTERVAL;
      this.currentMode = UPDATE_MODE.polling;
      return shortPollModel(this, config, interval).then(config.connected);
    } else {
      // Establish connection to Firehose
      this.currentMode = UPDATE_MODE.firehose;
      return this.activity.instance.start(config);
    }
  };

  _createClass(ActivityUpdater, [{
    key: "activity",
    get: function get() {
      return this.options.activity;
    }
  }, {
    key: "mode",
    get: function get() {
      return this.currentMode;
    }
  }, {
    key: "multiplex",
    get: function get() {
      return this.options.multiplex;
    }
  }, {
    key: "serviceAreaId",
    get: function get() {
      return this.options.serviceAreaId;
    }
  }]);

  return ActivityUpdater;
}();

export { ActivityUpdater as default };